import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
} from "react";
import { paymentService } from "../api/services/paymentService";
import { useAuth } from "./AuthContext";
// import { toast } from "react-toastify";

// Initial state
const initialState = {
  payments: [],
  loading: false,
  error: null,
  stats: {
    totalPayments: 0,
    totalAmount: 0,
    customerPayments: 0,
    vendorPayments: 0,
    completedPayments: 0,
    pendingPayments: 0,
    monthlyTotal: 0,
    weeklyTotal: 0,
  },
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  },
};

// Action types
const ActionTypes = {
  SET_LOADING: "SET_LOADING",
  SET_ERROR: "SET_ERROR",
  SET_PAYMENTS: "SET_PAYMENTS",
  ADD_PAYMENT: "ADD_PAYMENT",
  UPDATE_PAYMENT: "UPDATE_PAYMENT",
  DELETE_PAYMENT: "DELETE_PAYMENT",
  SET_STATS: "SET_STATS",
  SET_PAGINATION: "SET_PAGINATION",
  CLEAR_ERROR: "CLEAR_ERROR",
};

// Reducer
const paymentReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };

    case ActionTypes.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false,
      };

    case ActionTypes.SET_PAYMENTS:
      return {
        ...state,
        payments: action.payload,
        loading: false,
        error: null,
      };

    case ActionTypes.ADD_PAYMENT:
      return {
        ...state,
        payments: [action.payload, ...state.payments],
        loading: false,
        error: null,
      };

    case ActionTypes.UPDATE_PAYMENT:
      return {
        ...state,
        payments: state.payments.map((payment) =>
          payment.id === action.payload.id ? action.payload : payment
        ),
        loading: false,
        error: null,
      };

    case ActionTypes.DELETE_PAYMENT:
      return {
        ...state,
        payments: state.payments.filter(
          (payment) => payment.id !== action.payload
        ),
        loading: false,
        error: null,
      };

    case ActionTypes.SET_STATS:
      return {
        ...state,
        stats: action.payload,
      };

    case ActionTypes.SET_PAGINATION:
      return {
        ...state,
        pagination: action.payload,
      };

    case ActionTypes.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    default:
      return state;
  }
};

// Create context
export const PaymentContext = createContext();

// Provider component
export const PaymentProvider = ({ children }) => {
  const [state, dispatch] = useReducer(paymentReducer, initialState);
  const { user } = useAuth();

  // Helper function to handle API errors
  const handleError = (error, customMessage = null) => {
    console.error("Payment API Error:", error);
    const errorMessage =
      customMessage ||
      error.response?.data?.message ||
      error.message ||
      "An unexpected error occurred";

    dispatch({ type: ActionTypes.SET_ERROR, payload: errorMessage });
    // toast.error(errorMessage);
    console.error("Payment Error:", errorMessage);
    return errorMessage;
  };

  // Get all payments
  const getPayments = useCallback(async (filters = {}) => {
    try {
      dispatch({ type: ActionTypes.SET_LOADING, payload: true });

      const response = await paymentService.getAllPayments(filters);

      if (response.success) {
        dispatch({ type: ActionTypes.SET_PAYMENTS, payload: response.data });

        // Update pagination if provided
        if (response.pagination) {
          dispatch({
            type: ActionTypes.SET_PAGINATION,
            payload: response.pagination,
          });
        }
      } else {
        throw new Error(response.message || "Failed to fetch payments");
      }
    } catch (error) {
      handleError(error, "Failed to load payments");
    }
  }, []);

  // Get payment by ID
  const getPaymentById = useCallback(async (paymentId) => {
    try {
      dispatch({ type: ActionTypes.SET_LOADING, payload: true });

      const response = await paymentService.getPaymentById(paymentId);

      if (response.success) {
        dispatch({ type: ActionTypes.SET_LOADING, payload: false });
        return response.data;
      } else {
        throw new Error(response.message || "Failed to fetch payment");
      }
    } catch (error) {
      handleError(error, "Failed to load payment details");
      return null;
    }
  }, []);

  // Create new payment
  const createPayment = useCallback(async (paymentData) => {
    try {
      dispatch({ type: ActionTypes.SET_LOADING, payload: true });

      const response = await paymentService.createPayment(paymentData);

      if (response.success) {
        dispatch({ type: ActionTypes.ADD_PAYMENT, payload: response.data });
        // toast.success("Payment created successfully");
        console.log("Payment created successfully");
        return response.data;
      } else {
        throw new Error(response.message || "Failed to create payment");
      }
    } catch (error) {
      handleError(error, "Failed to create payment");
      return null;
    }
  }, []);

  // Update payment
  const updatePayment = useCallback(async (paymentId, paymentData) => {
    try {
      dispatch({ type: ActionTypes.SET_LOADING, payload: true });

      const response = await paymentService.updatePayment(
        paymentId,
        paymentData
      );

      if (response.success) {
        dispatch({ type: ActionTypes.UPDATE_PAYMENT, payload: response.data });
        // toast.success("Payment updated successfully");
        console.log("Payment updated successfully");
        return response.data;
      } else {
        throw new Error(response.message || "Failed to update payment");
      }
    } catch (error) {
      handleError(error, "Failed to update payment");
      return null;
    }
  }, []);

  // Delete payment
  const deletePayment = useCallback(async (paymentId) => {
    try {
      dispatch({ type: ActionTypes.SET_LOADING, payload: true });

      const response = await paymentService.deletePayment(paymentId);

      if (response.success) {
        dispatch({ type: ActionTypes.DELETE_PAYMENT, payload: paymentId });
        // toast.success("Payment deleted successfully");
        console.log("Payment deleted successfully");
        return true;
      } else {
        throw new Error(response.message || "Failed to delete payment");
      }
    } catch (error) {
      handleError(error, "Failed to delete payment");
      return false;
    }
  }, []);

  // Get payment statistics
  const getPaymentStats = useCallback(async (filters = {}) => {
    try {
      const response = await paymentService.getPaymentStats(filters);

      if (response.success) {
        dispatch({ type: ActionTypes.SET_STATS, payload: response.data });
        return response.data;
      } else {
        throw new Error(
          response.message || "Failed to fetch payment statistics"
        );
      }
    } catch (error) {
      handleError(error, "Failed to load payment statistics");
      return null;
    }
  }, []);

  // Get customer payments
  const getCustomerPayments = useCallback(async (customerId, filters = {}) => {
    try {
      dispatch({ type: ActionTypes.SET_LOADING, payload: true });

      const response = await paymentService.getCustomerPayments(
        customerId,
        filters
      );

      if (response.success) {
        dispatch({ type: ActionTypes.SET_LOADING, payload: false });
        return response.data;
      } else {
        throw new Error(
          response.message || "Failed to fetch customer payments"
        );
      }
    } catch (error) {
      handleError(error, "Failed to load customer payments");
      return [];
    }
  }, []);

  // Get vendor payments
  const getVendorPayments = useCallback(async (vendorId, filters = {}) => {
    try {
      dispatch({ type: ActionTypes.SET_LOADING, payload: true });

      const response = await paymentService.getVendorPayments(
        vendorId,
        filters
      );

      if (response.success) {
        dispatch({ type: ActionTypes.SET_LOADING, payload: false });
        return response.data;
      } else {
        throw new Error(response.message || "Failed to fetch vendor payments");
      }
    } catch (error) {
      handleError(error, "Failed to load vendor payments");
      return [];
    }
  }, []);

  // Process bulk payments
  const processBulkPayments = useCallback(
    async (payments) => {
      try {
        dispatch({ type: ActionTypes.SET_LOADING, payload: true });

        const response = await paymentService.processBulkPayments(payments);

        if (response.success) {
          // Refresh payments after bulk processing
          await getPayments();
          // toast.success(
          //   `${response.data.processedCount} payments processed successfully`
          // );
          return response.data;
        } else {
          throw new Error(
            response.message || "Failed to process bulk payments"
          );
        }
      } catch (error) {
        handleError(error, "Failed to process bulk payments");
        return null;
      }
    },
    [getPayments]
  );

  // Clear error
  const clearError = useCallback(() => {
    dispatch({ type: ActionTypes.CLEAR_ERROR });
  }, []);

  // Context value
  const value = {
    // State
    payments: state.payments,
    loading: state.loading,
    error: state.error,
    stats: state.stats,
    pagination: state.pagination,

    // Actions
    getPayments,
    getPaymentById,
    createPayment,
    updatePayment,
    deletePayment,
    getPaymentStats,
    getCustomerPayments,
    getVendorPayments,
    processBulkPayments,
    clearError,
  };

  return (
    <PaymentContext.Provider value={value}>{children}</PaymentContext.Provider>
  );
};

// Custom hook to use payment context
export const usePayment = () => {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error("usePayment must be used within a PaymentProvider");
  }
  return context;
};

export default PaymentContext;
